import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getClosestDayId } from "./useFestivalDay";
import type { Day } from "../types";

const getInitialStageId = (): string => {
  return "nagyszinpad";
};

export function useTimetableParams(days: Day[]) {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL query parameters parsing
  const urlDay = searchParams.get("day");
  const urlStage = searchParams.get("stage");
  const urlFavorites = searchParams.get("favorites");

  // Keep track of whether stage was initially specified in search params
  const hasStageInUrlRef = useRef(!!urlStage);

  // Local state for activeStageId synchronized with URL parameter
  const [activeStageId, setActiveStageIdState] = useState<string>(() => {
    return urlStage || getInitialStageId();
  });

  // Local state for activeDayId synchronized with URL parameter
  const [activeDayId, setActiveDayIdState] = useState<string>(() => {
    if (urlDay) return urlDay;
    try {
      const savedDay = localStorage.getItem("activeDayId");
      return savedDay ? JSON.parse(savedDay) : getClosestDayId(days);
    } catch {
      return getClosestDayId(days);
    }
  });

  // Local state for showFavoritesOnly synchronized with URL parameter
  const [showFavoritesOnly, setShowFavoritesOnlyState] = useState<boolean>(() => {
    return urlFavorites === "true";
  });

  const lastUrlDayRef = useRef(urlDay);
  const lastUrlStageRef = useRef(urlStage);
  const lastUrlFavoritesRef = useRef(urlFavorites);

  // On mount: synchronize URL params and localStorage based on the priority rules
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    // 1. Day
    if (urlDay) {
      try {
        localStorage.setItem("activeDayId", JSON.stringify(urlDay));
      } catch (e) {
        console.warn(e);
      }
    }

    // 2. Stage
    if (urlStage) {
      try {
        localStorage.setItem("activeStageId_v3", JSON.stringify(urlStage));
        localStorage.setItem("activeStageId_timestamp_v3", Date.now().toString());
      } catch (e) {
        console.warn(e);
      }
    }

    // 3. Favorites (No longer stored in localStorage)
  }, []); // Run exactly once on mount

  // Synchronize state back from URL query parameters (e.g. on direct navigation, back/forward button)
  useEffect(() => {
    if (urlDay !== lastUrlDayRef.current) {
      lastUrlDayRef.current = urlDay;
      if (urlDay) {
        setActiveDayIdState(urlDay);
      }
    }
  }, [urlDay]);

  useEffect(() => {
    if (urlStage !== lastUrlStageRef.current) {
      lastUrlStageRef.current = urlStage;
      if (urlStage) {
        setActiveStageIdState(urlStage);
      }
    }
  }, [urlStage]);

  useEffect(() => {
    if (urlFavorites !== lastUrlFavoritesRef.current) {
      lastUrlFavoritesRef.current = urlFavorites;
      if (urlFavorites !== null) {
        setShowFavoritesOnlyState(urlFavorites === "true");
      }
    }
  }, [urlFavorites]);

  // Set local state, update URL search params, & update localStorage
  const setActiveStageId = useCallback(
    (stageId: string) => {
      setActiveStageIdState(stageId);
      lastUrlStageRef.current = stageId;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("stage", stageId);
          next.set("day", activeDayId);
          return next;
        },
        { replace: true },
      );
      try {
        localStorage.setItem("activeStageId_v3", JSON.stringify(stageId));
        localStorage.setItem("activeStageId_timestamp_v3", Date.now().toString());
      } catch (e) {
        console.warn("Error saving activeStageId to localStorage:", e);
      }
    },
    [setSearchParams, activeDayId],
  );

  const setActiveDayId = useCallback(
    (dayId: string) => {
      setActiveDayIdState(dayId);
      lastUrlDayRef.current = dayId;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("day", dayId);
          next.set("stage", activeStageId);
          return next;
        },
        { replace: true },
      );
      try {
        localStorage.setItem("activeDayId", JSON.stringify(dayId));
      } catch (e) {
        console.warn("Error saving activeDayId to localStorage:", e);
      }
    },
    [setSearchParams, activeStageId],
  );

  const setShowFavoritesOnly = useCallback(
    (favOnly: boolean) => {
      setShowFavoritesOnlyState(favOnly);
      lastUrlFavoritesRef.current = String(favOnly);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const favStr = String(favOnly);
          if (next.get("favorites") !== favStr) {
            next.set("favorites", favStr);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    activeStageId,
    activeDayId,
    showFavoritesOnly,
    setActiveStageId,
    setActiveDayId,
    setShowFavoritesOnly,
    setActiveStageIdState,
    hasStageInUrl: hasStageInUrlRef.current,
  };
}
